// pages/EditLooseDiamond.jsx
import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

import {
  useGetLooseDiamondEntryByIdQuery,
  useUpdateLooseDiamondEntryMutation,
} from "../toolkitServices/looseDiamondApi";

import LooseDiamondForm from "../components/looseDiamond/LooseDiamondForm";


const CARD_HEIGHT_CLASS = "h-[40vh] sm:h-[40vh]";

/* utils */
const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt3 = (v) => (Math.round(num(v) * 1000) / 1000).toFixed(3);
const fmt0 = (v) => String(Math.round(num(v)));

const rowLooksFilled = (r = {}) =>
  !!(r?.diamondName || r?.diamondWt || r?.diamondPcs || r?.diamondDescription);

const defaultRow = {
  diamondName: null,
  diamondShape: null,
  diamondClarity: null,
  diamondColor: null,
  diamondSieve: null,
  diamondSize: null,
  diamondWt: "",
  diamondPcs: "",
  diamondDescription: "",
};

export const defaultLooseDiamondValues = {
  vendor: "",
  branch: "",
  counter: "",
  looseDiamonds: [defaultRow],
};

/* inflate/deflate if you later add edit-flow */
export function inflateDiamondEntryToForm(entry) {
  if (!entry) return defaultLooseDiamondValues;
  return {
    vendor:  entry.vendorId  ? { value: entry.vendorId,  label: entry.vendorName  || "" } : "",
    branch:  entry.branchId  ? { value: entry.branchId,  label: entry.branchName  || "" } : "",
    counter: entry.counterId ? { value: entry.counterId, label: entry.counterName || "" } : "",
    looseDiamonds: [
      {
        diamondName: entry.diamondId
          ? { value: entry.diamondId, label: entry.diamondName || "" }
          : null,
        diamondShape: entry.shape
          ? { value: entry.shape, label: entry.shapeName || "" }
          : null,
        diamondClarity: entry.clarity
          ? { value: entry.clarity, label: entry.clarityName || "" }
          : null,
        diamondColor: entry.color
          ? { value: entry.color, label: entry.colorName || "" }
          : null,
        diamondSieve: entry.sieve
          ? { value: entry.sieve, label: entry.sieveName || "" }
          : null,
        diamondSize: entry.size
          ? { value: entry.size, label: entry.sizeName || "" }
          : null,
        diamondWt: entry.ct ?? "",
        diamondPcs: entry.pcs ?? "",
        diamondDescription: entry.description || "",
      },
    ],
  };
}

function deflateDiamondRow(form) {
  const r = (form?.looseDiamonds && form.looseDiamonds[0]) || {};
  return {
    diamondId:   r.diamondName?.value || null,
    diamondName: r.diamondName || null,

    shape:   r.diamondShape || null,
    clarity: r.diamondClarity || null,
    color:   r.diamondColor || null,
    sieve:   r.diamondSieve || null,
    size:    r.diamondSize || null,

    unit: "carat",
    ct: r.diamondWt ?? 0,
    pcs: r.diamondPcs ?? 0,
    description: r.diamondDescription ?? "",

    vendorId:   form.vendor?.value  || null,
    vendorName: form.vendor?.label  || "",
    branchId:   form.branch?.value  || null,
    branchName: form.branch?.label  || "",
    counterId:  form.counter?.value || null,
    counterName:form.counter?.label || "",
  };
}
const EditLooseDiamond = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isFetching, isError } = useGetLooseDiamondEntryByIdQuery(id, { skip: !id });
  const [updateEntry, { isLoading }] = useUpdateLooseDiamondEntryMutation();

  const existingData = useMemo(() => {
    const entry = data?.data || data;
    return entry ? inflateDiamondEntryToForm(entry) : null;
  }, [data]);

  const onSubmitOverride = async (payload) => {
    try {
      await updateEntry({ id, payload }).unwrap();
      toast.success("Loose diamond updated");
      navigate("/stocks/loose-diamonds");
    } catch (e) {
      const msg = e?.data?.msg || e?.data?.message || e?.error || "Update failed";
      toast.error(msg);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-8xl mx-auto"
    >
      <div className="bg-white shadow-lg rounded-2xl border p-3">
        {isFetching ? (
          <div className="p-4 text-sm text-slate-600">Loading…</div>
        ) : isError || !existingData ? (
          <div className="p-4 text-sm text-rose-600">Entry not found.</div>
        ) : (
          <LooseDiamondForm
            existingData={existingData}
            onSubmitOverride={onSubmitOverride}
            submitLabel={isLoading ? "Saving..." : "Save Changes"}
          />
        )}
      </div>
    </motion.div>
  );
};

export default EditLooseDiamond;
